import {
  action,
  computed,
  IReactionDisposer,
  observable,
  reaction,
  runInAction,
} from 'mobx';
import * as React from 'react';
import { GTMReporters } from 'components/HOCs/withGtmLogger';
import { PlanType, ProductFamily } from 'enums';
import { BaseProduct, AdvancedPlansProductData, ProductData } from 'types';
import { PlanTypeClick } from 'services/BiLogger/BiLoggerTypes';
import { PlansPageProps } from './PlansPageProps';

export const tabSwitchDelay = 250;

export const usePlansPageController = (props: PlansPageProps) => {
  const controller = React.useMemo(() => new PlansPageController(props), []);
  React.useEffect(() => {
    return () => controller.destroy();
  }, []);
  return controller;
};
class PlansPageController {
  @observable isInTransition: boolean = false;
  @observable isRequestACallModalOpened: boolean = false;
  @observable planType!: PlanType;
  reactionDisposer!: IReactionDisposer;

  constructor(private readonly props: PlansPageProps) {
    this.planType = this.props.rootStore.appStore.planType;
    this.reactionDisposer = reaction(
      () => this.props.rootStore.appStore.planType,
      (planType) => {
        this.isInTransition = true;
        setTimeout(() => {
          runInAction(() => {
            this.isInTransition = false;
            this.planType = planType;
          });
        }, tabSwitchDelay);
      },
    );
    void this.notifyGTM();
  }

  private readonly eligibleForSecondUpgrade = (product: BaseProduct) =>
    product.isEnabledForSecondUpgrade;

  private readonly sortProducts = (products: ProductData[]) =>
    products.sort(
      (prod1: ProductData, prod2: ProductData) => prod2.order - prod1.order,
    );

  private readonly sortProductsForMobile = (products: BaseProduct[]) =>
    products.sort(
      (prod1: BaseProduct, prod2: BaseProduct) => prod1.order - prod2.order,
    );

  notifyGTM = async () => {
    const { reportGTM, rootStore } = this.props;
    const { storage } = rootStore.appStore;
    storage.getPPViewed().then(
      () => {},
      async () => {
        await storage.setPPViewed();
        reportGTM(GTMReporters.PackagePicker);
      },
    );
  };

  @computed
  get websiteProducts(): ProductData[] {
    const { products } = this.props.rootStore.appStore.appContext;
    return this.sortProducts(
      products.filter((product) => product.type === PlanType.Website),
    );
  }

  get shouldShowLimitedTimeBanner(): boolean {
    const {
      appContext: { isLimitedTimeDiscount },
      isInSale,
    } = this.props.rootStore.appStore;
    return !isInSale && isLimitedTimeDiscount && !this.isInTransition;
  }

  @computed
  get mobileWebsiteProducts(): BaseProduct[] {
    const {
      appContext: { advancedPlansProducts },
    } = this.props.rootStore.appStore;
    const productsToUse: BaseProduct[] = advancedPlansProducts;
    return this.sortProductsForMobile(
      productsToUse.filter(
        (product) =>
          product.type === PlanType.Website &&
          this.eligibleForSecondUpgrade(product),
      ),
    );
  }

  @computed
  get mobileBusinessProducts(): BaseProduct[] {
    const {
      appContext: { advancedPlansProducts },
    } = this.props.rootStore.appStore;
    const productsToUse: BaseProduct[] = advancedPlansProducts;
    return this.sortProductsForMobile(
      productsToUse.filter(
        (product) =>
          product.type === PlanType.Business &&
          this.eligibleForSecondUpgrade(product),
      ),
    );
  }

  @computed
  get businessProducts(): ProductData[] {
    const { products } = this.props.rootStore.appStore.appContext;
    return this.sortProducts(
      products.filter((product) => product.type === PlanType.Business),
    );
  }

  @computed
  get advancedPlansBusinessProducts(): AdvancedPlansProductData[] {
    const { advancedPlansProducts } = this.props.rootStore.appStore.appContext;
    return advancedPlansProducts.filter(
      ({ type }) => type === PlanType.Business,
    );
  }

  @computed
  get advancedPlansWebsiteProducts(): AdvancedPlansProductData[] {
    const { advancedPlansProducts } = this.props.rootStore.appStore.appContext;
    return advancedPlansProducts.filter(
      ({ type }) => type === PlanType.Website,
    );
  }

  @action
  websiteBannerCTA = (goToPlan: PlanType) => {
    const { scrollToTop, selectTab } = this.props.rootStore.appStore;
    if (this.hasEnterpriseProduct) {
      this.isRequestACallModalOpened = true;
      return;
    }
    scrollToTop();
    selectTab(goToPlan, PlanTypeClick.Bottom);
  };

  @action
  closeRequestACallModal = () => {
    this.props.rootStore.appStore.appContext.biLogger.logRequestCallPopupCTA({
      clickType: 'close',
    });
    this.isRequestACallModalOpened = false;
  };

  @action
  onSelectProduct = (product: BaseProduct) => {
    if (product.productFamily === ProductFamily.Enterprise) {
      this.isRequestACallModalOpened = true;
      return;
    }
    this.props.onSelectProduct(product);
  };

  @computed get hasEnterpriseProduct() {
    return !!this.advancedPlansBusinessProducts.find(
      (p) => p.productFamily === ProductFamily.Enterprise,
    );
  }

  destroy = () => {
    this.reactionDisposer();
  };
}
